/**
 * API Configuration
 *
 * Central configuration for API communication with the backend.
 * Supports environment-based configuration and feature flags.
 */

/**
 * API configuration interface
 */
export interface ApiConfig {
  BASE_URL: string
  USE_BACKEND: boolean
  TIMEOUT: number
  RETRY_ATTEMPTS: number
  RETRY_DELAY: number
}

/**
 * API configuration object
 */
export const API_CONFIG: ApiConfig = {
  /** Base URL for API requests */
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3007/api/v1',

  /** Whether to use backend API or static presentations */
  USE_BACKEND: import.meta.env.VITE_USE_BACKEND !== 'false',

  /** Request timeout in milliseconds */
  TIMEOUT: 10000,

  /** Number of retry attempts for failed requests */
  RETRY_ATTEMPTS: 3,

  /** Delay between retries in milliseconds */
  RETRY_DELAY: 1000,
} as const

/**
 * API endpoints configuration
 *
 * Uses the new presentation-library API with role-based access control.
 * All endpoints require authentication except for public metadata access.
 */
export const API_ENDPOINTS = {
  /** Get all presentations for the library (with role-based access control) */
  LIBRARY: '/presentation-library',

  /** Get a single presentation by ID (with access control check) */
  PRESENTATION_BY_ID: (id: string) => `/presentation-library/${id}`,

  /** Get a single presentation by slug (with access control check) */
  PRESENTATION_BY_SLUG: (slug: string) => `/presentation-library/slug/${slug}`,

  /** Grant access to a presentation (super admin only) */
  GRANT_ACCESS: (id: string) => `/presentation-library/${id}/access`,

  /** List access rules for a presentation (super admin only) */
  LIST_ACCESS: (id: string) => `/presentation-library/${id}/access`,

  /** Revoke access to a presentation (super admin only) */
  REVOKE_ACCESS: (id: string, accessId: string) => `/presentation-library/${id}/access/${accessId}`,

  /** Create a new presentation (authenticated) - NOT YET IMPLEMENTED */
  CREATE: '/presentation-library',

  /** Update a presentation by ID (authenticated) - NOT YET IMPLEMENTED */
  UPDATE: (id: string) => `/presentation-library/${id}`,

  /** Delete a presentation by ID (authenticated) - NOT YET IMPLEMENTED */
  DELETE: (id: string) => `/presentation-library/${id}`,
} as const

/**
 * Type for API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
  }
}

/**
 * Type for library API response
 */
export interface LibraryResponse {
  success: boolean
  presentations: Array<{
    slug: string
    data: import('../types').PresentationData
  }>
  total: number
}

/**
 * Type for presentation by slug API response
 */
export interface PresentationResponse {
  success: boolean
  data?: import('../types').PresentationData
  error?: {
    code: string
    message: string
  }
}
